import { ImResponse, messDoorClient, resolve } from 'services/api';
import type { PlatformRegions, SensitiveWordItem } from './types';

export interface GetSensitiveWordListParams {
  pageNum: number;
  pageSize: number;
}

export interface SearchSensitiveWordParams extends GetSensitiveWordListParams {
  keyword: string;
}

export interface SensitiveWordListResponse {
  list: SensitiveWordItem[];
  total: number;
}

interface RawSensitiveWordListResponse {
  words: SensitiveWordItem[];
  total: number;
}

export interface AddSensitiveWordConflictResponse {
  conflictType?: 'DUPLICATE' | 'CONFLICT';
  existingWord?: SensitiveWordItem;
}

export function getSensitiveWordList(params: GetSensitiveWordListParams) {
  return resolve(
    messDoorClient.get<ImResponse<RawSensitiveWordListResponse>>(
      '/censored/v1/sensitiveWord/list',
      {
        params,
      },
    ),
  ).then<SensitiveWordListResponse>((response) => ({
    list: response.words.map(({ platformRegions = {}, ...others }) => {
      return {
        ...others,
        platformRegions: Object.keys(platformRegions).reduce<PlatformRegions>((res, key) => {
          res[key] = (res[key] as any).regions;
          return res;
        }, platformRegions),
      };
    }),
    total: response.total,
  }));
}

export function searchSensitiveWord(params: SearchSensitiveWordParams) {
  return resolve(
    messDoorClient.get<ImResponse<RawSensitiveWordListResponse>>(
      '/censored/v1/sensitiveWord/search',
      {
        params,
      },
    ),
  ).then<SensitiveWordListResponse>((response) => ({
    list: response.words.map(({ platformRegions = {}, ...others }) => {
      return {
        ...others,
        platformRegions: Object.keys(platformRegions).reduce<PlatformRegions>((res, key) => {
          res[key] = (res[key] as any).regions;
          return res;
        }, platformRegions),
      };
    }),
    total: response.total,
  }));
}

export function addSensitiveWord({ platformRegions = {}, ...others }: Partial<SensitiveWordItem>) {
  return resolve(
    messDoorClient.post<ImResponse<AddSensitiveWordConflictResponse>>(
      '/censored/v1/sensitiveWord/scope',
      {
        ...others,
        platformRegions: Object.keys(platformRegions).reduce<any>(
          (res, key) => {
            res[key] = { regions: res[key] };
            return res;
          },
          { ...platformRegions },
        ),
      },
    ),
  );
}

export function editSensitiveWord({ id, platformRegions = {}, ...others }: SensitiveWordItem) {
  return resolve(
    messDoorClient.put<ImResponse<void>>(`/censored/v1/sensitiveWord/${id}`, {
      ...others,
      platformRegions: Object.keys(platformRegions).reduce<any>(
        (res, key) => {
          res[key] = { regions: res[key] };
          return res;
        },
        { ...platformRegions },
      ),
      updateScope: true,
    }),
  );
}

export function deleteSensitiveWord(wordId: number) {
  return resolve(messDoorClient.delete<ImResponse<void>>(`/censored/v1/sensitiveWord/${wordId}`));
}
